import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import styled from 'styled-components';
import { LuUpload } from 'react-icons/lu';

import { useToast } from '@contexts/Toast.context';
import { validateVideoFile } from '@utils/video/helpers';
import {
	flow,
	panelCss,
	Muted,
	Small,
} from '@routes/userRoutes/projects/videoProject/flow/primitives';

/** Step 1, empty state: the drop zone fills the video area. Same rules as before: one MP4/MOV, up to 10 GB. */

const Zone = styled.div`
	${panelCss};
	position: relative;
	width: 100%;
	aspect-ratio: 16 / 9;
	flex: none;
	display: flex;
	padding: 1.375rem;
	cursor: pointer;
	&:focus-visible {
		outline: 2px solid ${({ theme }) => theme.colors.lightGreen};
		outline-offset: 2px;
	}
`;

const Dashed = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 0.875rem;
	text-align: center;
	border: 2px dashed ${({ $active, theme }) => ($active ? theme.colors.green : '#3b403b')};
	border-radius: ${flow.radius};
	background: ${({ $active }) =>
		$active ? 'rgba(32, 172, 107, 0.08)' : 'rgba(32, 172, 107, 0.035)'};
	transition: all 0.2s;
`;

const Ring = styled.span`
	width: 74px;
	height: 74px;
	border-radius: 50%;
	background: ${flow.greenFill};
	color: ${({ theme }) => theme.colors.green};
	display: grid;
	place-items: center;
`;

const Big = styled.h2`
	margin: 0;
	font-size: 1.375rem;
	font-weight: 700;
	color: ${flow.text};
`;

const MESSAGES = {
	'too-many-files': 'Please select only one video file.',
	'file-too-large': 'Video file is too large. Maximum size is 10 GB.',
	'file-invalid-type': 'Only MP4 and MOV files are supported.',
};

const FlowDropZone = ({ onFile }) => {
	const { addToast } = useToast();
	const [checking, setChecking] = useState(false);

	const onAccepted = useCallback(
		async ([file]) => {
			setChecking(true);
			try {
				const v = await validateVideoFile(file);
				if (v.valid) onFile(file);
				else
					addToast({
						id: 'video-validation-error',
						title: 'This video cannot be used',
						message: v.error,
						bg: 'danger',
					});
			} catch (err) {
				addToast({
					id: 'video-validation-error',
					title: 'This video cannot be used',
					message: err?.message || 'Unexpected error while checking the file.',
					bg: 'danger',
				});
			} finally {
				setChecking(false);
			}
		},
		[onFile, addToast]
	);

	const onRejected = useCallback(
		rejections => {
			const code = rejections[0]?.errors[0]?.code;
			addToast({
				id: `video-file-error-${code}`,
				title: 'File not accepted',
				message: MESSAGES[code] || 'This file cannot be used.',
				bg: 'danger',
			});
		},
		[addToast]
	);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDropAccepted: onAccepted,
		onDropRejected: onRejected,
		accept: { 'video/mp4': ['.mp4'], 'video/quicktime': ['.mov'] },
		maxFiles: 1,
		maxSize: 10 * 1024 * 1024 * 1024,
		multiple: false,
		disabled: checking,
	});

	return (
		<Zone {...getRootProps({ 'aria-label': 'Choose a drone video' })}>
			<input {...getInputProps()} />
			<Dashed $active={isDragActive}>
				<Ring aria-hidden="true">
					<LuUpload size={32} />
				</Ring>
				<Big>
					{checking
						? 'Checking the video…'
						: isDragActive
							? 'Drop it here'
							: 'Drop your drone video here'}
				</Big>
				<Muted>
					or <span style={{ color: '#20ac6b', fontWeight: 600 }}>browse files</span> · MP4
					/ MOV, up to 10 GB
				</Muted>
				<Small>
					H.264 plays in every browser · a camera pointing straight down gives the best
					counts
				</Small>
			</Dashed>
		</Zone>
	);
};

export default FlowDropZone;
